import React, { useState, useEffect } from 'react';
import { X, Clock, User } from 'lucide-react';
import { crmDataService } from '../services/crmDataService';

interface DealDetailModalProps {
  deal: any;
  currentUser: any;
  onClose: () => void;
  globalRefreshKey?: number;
  onRefresh?: () => void;
}

const DealDetailModal: React.FC<DealDetailModalProps> = ({ deal, onClose, globalRefreshKey }) => {
  const [activities, setActivities] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      setIsLoading(true);
      try {
        // 1. 홈 대시보드와 동일한 '검증된 피드 통로' 사용
        const allFeeds = await crmDataService.getActivityFeed(0);
        
        if (allFeeds && Array.isArray(allFeeds)) {
          // 2. 🔍 강력한 매칭 로직 (ID 일치 OR 이름 포함)
          const currentDealId = String(deal.id || '').trim();
          const currentCompanyName = String(deal['거래처'] || '').trim().replace(/\s+/g, '');

          const matchedFeeds = allFeeds.filter((feed: any) => {
            const feedDealId = String(feed.deal_id || '').trim();
            const feedCompanyNameInId = feedDealId.replace(/\s+/g, '');
            const feedContent = String(feed.content || '');

            // 상태 변경 로그는 걸러내고 싶으시면 여기에 필터를 걸 수 있습니다.
            // 현재는 모든 '내용(content)'이 있는 기록을 가져옵니다.
            return (
              feedDealId === currentDealId || 
              (currentCompanyName !== '' && feedCompanyNameInId.includes(currentCompanyName)) ||
              (currentCompanyName !== '' && feedContent.includes(deal['거래처']))
            );
          });

          // 3. 최신순 정렬
          setActivities(matchedFeeds.sort((a: any, b: any) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          ));
        }
      } catch (error) {
        console.error("히스토리 로딩 실패:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (deal) fetchHistory();
  }, [deal, globalRefreshKey]);

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden animate-in zoom-in duration-200">
        
        {/* 모달 헤더 */}
        <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <div>
            <h2 className="text-lg font-black text-slate-800">{deal['거래처']}</h2>
            <p className="text-sm text-slate-500 font-bold mt-1">
              건명: {deal['건명'] || '-'} | 담당자: {deal['담당자']}
            </p>
          </div>
          <button onClick={onClose} className="p-2 bg-slate-200 text-slate-600 rounded-full hover:bg-slate-300 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* 히스토리 피드 영역 */}
        <div className="p-6 flex-1 overflow-y-auto custom-scrollbar bg-slate-50/50">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={18} className="text-indigo-600" />
            <h3 className="text-sm font-black text-slate-700">실시간 영업 피드 (히스토리)</h3>
          </div>

          {isLoading ? (
            <div className="text-center py-10 text-slate-500 font-bold animate-pulse">
              데이터를 불러오는 중입니다...
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-xl border border-dashed border-slate-300 text-slate-500 font-bold">
              아직 등록된 영업 활동(피드)이 없습니다.
            </div>
          ) : (
            <div className="space-y-4">
              {activities.map((act, index) => {
                // CS팀 여부 판단
                const isCS = String(act.editor || '').includes('CS');

                return (
                  <div key={index} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col gap-2 hover:border-indigo-300 transition-all">
                    <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded-md ${
                        isCS ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'
                      }`}>
                        {isCS ? 'CS 활동' : '영업 기록'}
                      </span>
                      <span className="text-[11px] text-slate-400 font-bold">
                        {act.created_at ? new Date(act.created_at).toLocaleString() : '기록일 없음'}
                      </span>
                    </div>

                    <p className="text-sm text-slate-700 whitespace-pre-wrap mt-1 leading-relaxed">
                      {act.content || '내용 없음'}
                    </p>

                    <div className="flex items-center gap-1 mt-1 text-[11px] text-slate-500 font-bold">
                      <User size={12} className="text-slate-400" /> 
                      <span>작성자: {act.editor || '담당자'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealDetailModal;